import MyPoint from "./MyPoint";

/**
 * Vector - класс для работы с 2D векторами
 * JavaScript версия Vector из VectorGeometryLib
 * Обновлено для соответствия актуальной версии
 */
class Vector {
    /**
     * Конструктор вектора
     * @param {MyPoint} start - начальная точка
     * @param {MyPoint} end - конечная точка
     */
    constructor(start, end) {
        this.startPoint = start;
        this.endPoint = end;
        this._cachedDelta = null;
        this._cachedLength = null;
        this._cachedK = null;
        this._cachedB = null;
        this.color = null;
        this.isDashed = false;
        this.tooltip = '';
    }
    /**
     * Конструктор вектора с дополнительными параметрами
     * @param {MyPoint} start - начальная точка
     * @param {MyPoint} end - конечная точка
     * @param {string} color - цвет
     * @param {boolean} isDashed - пунктирная линия
     * @param {string} tooltip - всплывающая подсказка
     */
    static createWithPolylineParams(start, end, color, isDashed, tooltip) {
        const vector = new Vector(start, end);
        vector.color = color;
        vector.isDashed = isDashed;
        vector.tooltip = tooltip;
        return vector;
    }

    /**
     * Конструктор вектора из координат
     * @param {number} startX - X координата начальной точки
     * @param {number} startY - Y координата начальной точки
     * @param {number} endX - X координата конечной точки
     * @param {number} endY - Y координата конечной точки
     */
    static fromCoordinates(startX, startY, endX, endY) {
        return new Vector(new MyPoint(startX, startY), new MyPoint(endX, endY));
    }

    /**
     * Константа для сравнения с плавающей точкой
     */
    static get EPSILON() {
        return 1e-6;
    }

    /**
     * Начальная точка
     */
    get StartPoint() {
        return this.startPoint;
    }

    /**
     * Конечная точка
     */
    get EndPoint() {
        return this.endPoint;
    }

    /**
     * Вектор смещения (end - start)
     */
    get Delta() {
        if (!this._cachedDelta) {
            this._cachedDelta = this.endPoint.subtract(this.startPoint);
        }
        return this._cachedDelta;
    }

    /**
     * Длина вектора
     */
    get Length() {
        if (this._cachedLength === null) {
            this._cachedLength = this.Delta.Length;
        }
        return this._cachedLength;
    }

    /**
     * Квадрат длины вектора
     */
    get LengthSquared() {
        return this.Delta.LengthSquared;
    }

    /**
     * Проверка вертикальности вектора
     */
    get IsVertical() {
        return Math.abs(this.Delta.x) < Vector.EPSILON;
    }

    /**
     * Проверка горизонтальности вектора
     */
    get IsHorizontal() {
        return Math.abs(this.Delta.y) < Vector.EPSILON;
    }

    /**
     * Проверка нулевого вектора
     */
    get IsZero() {
        return this.LengthSquared < Vector.EPSILON * Vector.EPSILON;
    }

    /**
     * Угловой коэффициент (k) для уравнения прямой y = kx + b
     */
    get K() {
        if (this._cachedK === null) {
            if (this.IsVertical) {
                this._cachedK = Infinity;
            } else {
                this._cachedK = this.Delta.y / this.Delta.x;
            }
        }
        return this._cachedK;
    }

    /**
     * Свободный член (b) для уравнения прямой y = kx + b
     */
    get B() {
        if (this._cachedB === null) {
            if (this.IsVertical) {
                this._cachedB = this.startPoint.x;
            } else {
                this._cachedB = this.startPoint.y - this.K * this.startPoint.x;
            }
        }
        return this._cachedB;
    }

    /**
     * Нормализованный вектор
     */
    get Normalized() {
        const length = this.Length;
        if (length < Vector.EPSILON) {
            return new Vector(this.startPoint, this.startPoint);
        }
        return new Vector(this.startPoint, this.startPoint.add(this.Delta.Normalized));
    }

    /**
     * Перпендикулярный вектор
     */
    get Perpendicular() {
        return new Vector(this.startPoint, this.startPoint.add(new MyPoint(-this.Delta.y, this.Delta.x)));
    }

    /**
     * Перпендикулярный вектор от начальной точки
     * @param {number} length - длина перпендикуляра
     * @returns {Vector} перпендикулярный вектор
     */
    perpendicularStart(length) {
        if (this.IsZero) {
            return new Vector(this.startPoint, this.startPoint);
        }
        const perp = new MyPoint(-this.Delta.y, this.Delta.x).Normalized;
        return new Vector(this.startPoint, this.startPoint.add(perp.multiply(length)));
    }

    /**
     * Перпендикулярный вектор от конечной точки
     * @param {number} length - длина перпендикуляра
     * @returns {Vector} перпендикулярный вектор
     */
    perpendicularEnd(length) {
        if (this.IsZero) {
            return new Vector(this.endPoint, this.endPoint);
        }
        const perp = new MyPoint(-this.Delta.y, this.Delta.x).Normalized;
        return new Vector(this.endPoint, this.endPoint.add(perp.multiply(length)));
    }

    /**
     * Параллельный вектор со смещением
     * @param {number} offset - смещение
     * @returns {Vector} параллельный вектор
     */
    parallelVector(offset) {
        if (this.IsZero) {
            return new Vector(this.startPoint, this.endPoint);
        }
        const perp = new MyPoint(-this.Delta.y, this.Delta.x).Normalized;
        const offsetVector = perp.multiply(offset);
        return new Vector(
            this.startPoint.add(offsetVector),
            this.endPoint.add(offsetVector)
        );
    }

    /**
     * Вектор вдоль направления с заданной длиной
     * @param {number} length - длина
     * @returns {Vector} вектор с заданной длиной
     */
    along(length) {
        const normalized = this.Delta.Normalized;
        return new Vector(this.startPoint, this.startPoint.add(normalized.multiply(length)));
    }

    /**
     * Вектор с новой начальной точкой
     * @param {MyPoint} newStart - новая начальная точка
     * @returns {Vector} новый вектор
     */
    withStart(newStart) {
        return new Vector(newStart, newStart.add(this.Delta));
    }

    /**
     * Вектор с новой конечной точкой
     * @param {MyPoint} newEnd - новая конечная точка
     * @returns {Vector} новый вектор
     */
    withEnd(newEnd) {
        return new Vector(newEnd.subtract(this.Delta), newEnd);
    }

    /**
     * ОПТИМИЗИРОВАННЫЙ: Вычисляет точку пересечения с другим вектором
     * Использует детерминант матрицы для более эффективного вычисления
     * @param {Vector} of - другой вектор
     * @returns {MyPoint} точка пересечения
     */
    cross(of) {
        // Используем параметрическое представление линий:
        // L1: P1 + t1 * D1, где P1 = StartPoint, D1 = Delta
        // L2: P2 + t2 * D2, где P2 = of.StartPoint, D2 = of.Delta
        
        const p1 = this.startPoint;
        const p2 = of.startPoint;
        const d1 = this.Delta;
        const d2 = of.Delta;
        
        // Вычисляем детерминант матрицы 2x2
        const det = d1.x * d2.y - d1.y * d2.x;
        
        // Если детерминант близок к нулю, линии параллельны
        if (Math.abs(det) < Vector.EPSILON) {
            // Проверяем, совпадают ли линии
            const diff = p2.subtract(p1);
            const crossDiff = d1.x * diff.y - d1.y * diff.x;
            
            if (Math.abs(crossDiff) < Vector.EPSILON) {
                // Линии совпадают, возвращаем среднюю точку
                return this.midpoint(p1, p2);
            }
            
            // Линии параллельны, но не совпадают
            return new MyPoint(NaN, NaN);
        }
        
        // Вычисляем параметр t1 для первой линии
        const diff2 = p2.subtract(p1);
        const t1 = (diff2.x * d2.y - diff2.y * d2.x) / det;
        
        // Вычисляем точку пересечения
        return p1.add(d1.multiply(t1));
    }

    /**
     * Вспомогательный метод для вычисления средней точки
     * @param {MyPoint} a - первая точка
     * @param {MyPoint} b - вторая точка
     * @returns {MyPoint} средняя точка
     */
    midpoint(a, b) {
        return new MyPoint((a.x + b.x) * 0.5, (a.y + b.y) * 0.5);
    }

    /**
     * Проверка пересечения с другим вектором
     * @param {Vector} other - другой вектор
     * @returns {boolean} true если векторы пересекаются
     */
    intersects(other) {
        const intersection = this.cross(other);
        return !isNaN(intersection.x);
    }

    /**
     * Проверка пересечения сегментов
     * @param {Vector} other - другой вектор
     * @returns {boolean} true если сегменты пересекаются
     */
    intersectsSegment(other) {
        const intersection = this.cross(other);
        if (isNaN(intersection.x)) return false;

        return this.isPointOnSegment(intersection) && other.isPointOnSegment(intersection);
    }

    /**
     * Проверка принадлежности точки сегменту
     * @param {MyPoint} point - точка
     * @returns {boolean} true если точка на сегменте
     */
    isPointOnSegment(point) {
        if (!this.isPointOnLine(point)) return false;

        const d1 = this.startPoint.distanceTo(point);
        const d2 = this.endPoint.distanceTo(point);
        const segmentLength = this.Length;

        return Math.abs(d1 + d2 - segmentLength) < Vector.EPSILON;
    }

    /**
     * Проверка принадлежности точки прямой
     * @param {MyPoint} point - точка
     * @returns {boolean} true если точка на прямой
     */
    isPointOnLine(point) {
        if (this.IsVertical) {
            return Math.abs(point.x - this.startPoint.x) < Vector.EPSILON;
        }

        const expectedY = this.K * point.x + this.B;
        return Math.abs(point.y - expectedY) < Vector.EPSILON;
    }

    /**
     * Расстояние от точки до вектора
     * @param {MyPoint} point - точка
     * @returns {number} расстояние
     */
    distanceToPoint(point) {
        if (this.IsZero) {
            return this.startPoint.distanceTo(point);
        }

        const t = Math.max(0, Math.min(1, 
            point.subtract(this.startPoint).dot(this.Delta) / this.LengthSquared
        ));

        const projection = this.startPoint.add(this.Delta.multiply(t));
        return point.distanceTo(projection);
    }

    /**
     * Получение точки пересечения с другим вектором (устаревший метод)
     * @param {Vector} other - другой вектор
     * @returns {MyPoint|null} точка пересечения или null
     */
    getIntersection(other) {
        // Используем новый оптимизированный метод cross
        const intersection = this.cross(other);
        return isNaN(intersection.x) ? null : intersection;
    }

    /**
     * Сброс кэша при изменении точек
     */
    invalidateCache() {
        this._cachedDelta = null;
        this._cachedLength = null;
        this._cachedK = null;
        this._cachedB = null;
    }

    /**
     * Сброс вектора с новыми точками (для использования в пуле)
     * @param {MyPoint} start - новая начальная точка
     * @param {MyPoint} end - новая конечная точка
     */
    reset(start, end) {
        this.startPoint = start;
        this.endPoint = end;
        this.invalidateCache();
    }

    /**
     * Сброс вектора с новыми координатами
     * @param {number} startX - X координата начальной точки
     * @param {number} startY - Y координата начальной точки
     * @param {number} endX - X координата конечной точки
     * @param {number} endY - Y координата конечной точки
     */
    resetCoordinates(startX, startY, endX, endY) {
        this.startPoint = new MyPoint(startX, startY);
        this.endPoint = new MyPoint(endX, endY);
        this.invalidateCache();
    }

    /**
     * Статические методы
     */

    /**
     * Угол между двумя векторами
     * @param {Vector} a - первый вектор
     * @param {Vector} b - второй вектор
     * @returns {number} угол в радианах
     */
    static angleBetween(a, b) {
        const dot = a.Delta.dot(b.Delta);
        const det = a.Delta.cross(b.Delta);
        return Math.atan2(det, dot);
    }

    /**
     * Скалярное произведение двух векторов
     * @param {Vector} a - первый вектор
     * @param {Vector} b - второй вектор
     * @returns {number} скалярное произведение
     */
    static dot(a, b) {
        return a.Delta.dot(b.Delta);
    }

    /**
     * Векторное произведение двух векторов
     * @param {Vector} a - первый вектор
     * @param {Vector} b - второй вектор
     * @returns {number} векторное произведение
     */
    static cross(a, b) {
        return a.Delta.cross(b.Delta);
    }

    /**
     * Проверка перпендикулярности векторов
     * @param {Vector} a - первый вектор
     * @param {Vector} b - второй вектор
     * @returns {boolean} true если векторы перпендикулярны
     */
    static arePerpendicular(a, b) {
        return Math.abs(Vector.dot(a, b)) < Vector.EPSILON;
    }

    /**
     * Проверка параллельности векторов
     * @param {Vector} a - первый вектор
     * @param {Vector} b - второй вектор
     * @returns {boolean} true если векторы параллельны
     */
    static areParallel(a, b) {
        if (a.IsVertical || b.IsVertical) {
            return a.IsVertical && b.IsVertical;
        }
        return Math.abs(a.K - b.K) < Vector.EPSILON;
    }

    /**
     * Проверка коллинеарности векторов
     * @param {Vector} a - первый вектор
     * @param {Vector} b - второй вектор
     * @returns {boolean} true если векторы коллинеарны
     */
    static areCollinear(a, b) {
        return Math.abs(Vector.cross(a, b)) < Vector.EPSILON;
    }

    /**
     * Площадь треугольника
     * @param {MyPoint} a - первая точка
     * @param {MyPoint} b - вторая точка
     * @param {MyPoint} c - третья точка
     * @returns {number} площадь треугольника
     */
    static triangleArea(a, b, c) {
        return Math.abs(b.subtract(a).cross(c.subtract(a))) / 2;
    }

    /**
     * Площадь многоугольника
     * @param {MyPoint[]} points - массив точек
     * @returns {number} площадь многоугольника
     */
    static polygonArea(points) {
        if (points.length < 3) return 0;

        let area = 0;
        for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += points[i].x * points[j].y;
            area -= points[j].x * points[i].y;
        }
        return Math.abs(area) / 2;
    }

    /**
     * Строковое представление вектора
     * @returns {string} строка в формате "Vector: (x1, y1) -> (x2, y2)"
     */
    toString() {
        return `Vector: ${this.startPoint} -> ${this.endPoint} (Length: ${this.Length.toFixed(2)})`;
    }

    /**
     * Проверка равенства векторов
     * @param {Vector} other - другой вектор
     * @returns {boolean} true если векторы равны
     */
    equals(other) {
        if (!other) return false;
        return this.startPoint.equals(other.startPoint) && this.endPoint.equals(other.endPoint);
    }
}

export default Vector;